import { useState, useCallback, useEffect } from "react";

interface UseTextToSpeechOptions {
  rate?: number;
  pitch?: number;
  volume?: number;
  lang?: string;
}

interface UseTextToSpeechReturn {
  speak: (text: string) => void;
  stop: () => void;
  pause: () => void;
  resume: () => void;
  isSupported: boolean;
  isSpeaking: boolean;
  isLoading: boolean;
  isPaused: boolean;
  voices: SpeechSynthesisVoice[];
  error: string | null;
  browserSupported: boolean;
  checkSupport: () => void;
}

export const useTextToSpeech = (options: UseTextToSpeechOptions = {}): UseTextToSpeechReturn => {
  const [isSupported] = useState(() => 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window);
  const [browserSupported, setBrowserSupported] = useState(() => {
    // More comprehensive browser support check
    return 'speechSynthesis' in window &&
      'SpeechSynthesisUtterance' in window &&
      typeof SpeechSynthesisUtterance === 'function';
  });
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [error, setError] = useState<string | null>(null);

  const {
    rate = 1,
    pitch = 1,
    volume = 1,
    lang = 'en-US'
  } = options;

  useEffect(() => {
    if (!isSupported) {
      setError('Text-to-speech is not supported in this browser. Please use a modern browser like Chrome, Firefox, Safari, or Edge.');
      return;
    }

    const loadVoices = () => {
      const availableVoices = speechSynthesis.getVoices();
      if (availableVoices.length === 0) {
        // Some browsers load voices asynchronously
        setTimeout(() => {
          const updatedVoices = speechSynthesis.getVoices();
          setVoices(updatedVoices);
          if (updatedVoices.length === 0) {
            setError('No speech synthesis voices available. Please check your browser settings.');
          }
        }, 100);
      } else {
        setVoices(availableVoices);
      }
    };

    loadVoices();
    speechSynthesis.addEventListener('voiceschanged', loadVoices);

    return () => {
      speechSynthesis.removeEventListener('voiceschanged', loadVoices);
    };
  }, [isSupported]);

  const checkSupport = useCallback(() => {
    const supported = 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
    setBrowserSupported(supported);

    if (!supported) {
      setError('Text-to-speech requires a modern browser. Please update your browser or enable speech synthesis in your browser settings.');
    } else if (voices.length === 0) {
      setError('No speech synthesis voices available. Please check your browser settings and ensure speech synthesis is enabled.');
    } else {
      setError(null);
    }

    return supported;
  }, [voices.length]);

  const speak = useCallback((text: string) => {
    if (!isSupported) {
      setError('Text-to-speech is not supported in this browser');
      return;
    }

    if (!browserSupported) {
      setError('Speech synthesis is not available. Please check your browser settings.');
      return;
    }

    if (!text || !text.trim()) {
      setError('No text provided to speak');
      return;
    }

    if (voices.length === 0) {
      setError('No speech synthesis voices available. Please wait for voices to load or check your browser settings.');
      return;
    }

    // Committed to speaking — show loading until the utterance actually starts
    setIsLoading(true);

    // Stop any ongoing speech
    speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);

    // Apply options
    utterance.rate = rate;
    utterance.pitch = pitch;
    utterance.volume = volume;
    utterance.lang = lang;

    // Try to find a good voice (prefer English voices)
    const preferredVoices = voices.filter(voice =>
      voice.lang.startsWith('en') && voice.localService
    );

    if (preferredVoices.length > 0) {
      utterance.voice = preferredVoices[0];
    } else if (voices.length > 0) {
      // Fallback to any available voice
      utterance.voice = voices[0];
    }

    // Event listeners
    utterance.onstart = () => {
      setIsLoading(false);
      setIsSpeaking(true);
      setIsPaused(false);
      setError(null);
    };

    utterance.onend = () => {
      setIsLoading(false);
      setIsSpeaking(false);
      setIsPaused(false);
    };

    utterance.onerror = (event) => {
      setIsLoading(false);
      setIsSpeaking(false);
      setIsPaused(false);

      // Handle different error types - error codes may vary between browsers
      const errorCode = event.error as string;
      let errorMessage = 'Speech synthesis error occurred';

      if (errorCode) {
        if (errorCode.includes('network')) {
          errorMessage = 'Network error while loading speech synthesis. Please check your connection.';
        } else if (errorCode.includes('not-allowed') || errorCode.includes('permission')) {
          errorMessage = 'Speech synthesis is blocked. Please allow microphone/speech permissions.';
        } else if (errorCode.includes('no-speech') || errorCode.includes('speech')) {
          errorMessage = 'No speech was generated. Please try again.';
        } else if (errorCode.includes('aborted') || errorCode.includes('cancel')) {
          errorMessage = 'Speech synthesis was cancelled.';
        } else if (errorCode.includes('audio-busy') || errorCode.includes('busy')) {
          errorMessage = 'Audio system is busy. Please wait and try again.';
        } else if (errorCode.includes('audio-hardware') || errorCode.includes('hardware')) {
          errorMessage = 'Audio hardware error. Please check your audio settings.';
        } else if (errorCode.includes('language') || errorCode.includes('lang')) {
          errorMessage = 'The selected language is not supported by your browser.';
        } else {
          errorMessage = `Speech synthesis error: ${errorCode}`;
        }
      }

      setError(errorMessage);
    };

    utterance.onpause = () => {
      setIsPaused(true);
    };

    utterance.onresume = () => {
      setIsPaused(false);
    };

    try {
      speechSynthesis.speak(utterance);
    } catch (err) {
      setIsLoading(false);
      setError('Failed to start speech synthesis. Please try again.');
      console.error('TTS Error:', err);
    }
  }, [isSupported, browserSupported, rate, pitch, volume, lang, voices]);

  const stop = useCallback(() => {
    if (isSupported) {
      speechSynthesis.cancel();
      setIsLoading(false);
      setIsSpeaking(false);
      setIsPaused(false);
      setError(null);
    }
  }, [isSupported]);

  const pause = useCallback(() => {
    if (isSupported && isSpeaking) {
      speechSynthesis.pause();
    }
  }, [isSupported, isSpeaking]);

  const resume = useCallback(() => {
    if (isSupported && isPaused) {
      speechSynthesis.resume();
    }
  }, [isSupported, isPaused]);

  return {
    speak,
    stop,
    pause,
    resume,
    isSupported,
    browserSupported,
    isSpeaking,
    isLoading,
    isPaused,
    voices,
    error,
    checkSupport
  };
};
